import { createSelector } from "reselect"
import { get, first, last, chain, find, isNil } from "lodash"
import { getAllResourcesStatuses } from "selectors/data"
import { getStartDate, getEndDate, getSelectedDate } from "selectors/dates"
import { getForecasts } from "modules/portfolio-root/selectors/forecast"
import { getPeriod } from "modules/portfolio-root/selectors/portfolios"
import { getLatestDate } from "modules/portfolio-root/selectors/dates"
import { combineResourcesStatuses, isLoading } from "helpers/data-statuses"
import { getDatesAdjusted as getDatesAdjustedHelper } from "helpers/dates"

const defaultForecastData = {}
const getForecastData = state => get(state, "global.data.dailyForecast.data.model.simulation.dailyForecast", defaultForecastData)
const getCurrentForecastId = (state, props) => get(props, ["params", "forecastId"])

function formatForecast(forecast) {
  const count = forecast.length
  return chain(forecast)
    .map(({ date, expectedReturn }, index) => {
      if (!isNil(expectedReturn) || index === count - 1) {
        return ({
          date,
          factors: {
            expectedReturn: expectedReturn / 100,
          },
        })
      }
      return null
    })
    .filter(data => data)
    .value()
}

const getForecastEndDate = createSelector(
  [getForecastData],
  (data) => {
    return get(last(data), "date")
  },
)

export const getCurrentForecast = createSelector(
  [getForecasts, getCurrentForecastId],
  (forecasts, forecastId) => find(forecasts, ({ id }) => id === forecastId),
)

export const getForecast = createSelector(
  [getForecastData],
  (absolute) => formatForecast(absolute || {}),
)

export const getFilteredForecast = createSelector(
  [getForecast, getStartDate, getEndDate],
  (forecast, startDate, endDate) =>
    forecast.filter((data) => {
      if (data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getFirst = createSelector(
  [getForecast],
  (forecast) => first(forecast),
)

export const getCurrent = createSelector(
  [getForecast],
  (forecast) => last(forecast),
)

export const getFirstForecastDate = createSelector(
  [getFilteredForecast],
  (forecast) => get(first(forecast), "date", null),
)

export const getAdjustedEndDate = createSelector(
  [getEndDate, getForecastEndDate],
  (endDate, forecastEndDate) => forecastEndDate < endDate ? forecastEndDate : endDate,
)

export const getAdjustedSelectedDate = createSelector(
  [getSelectedDate, getForecastEndDate],
  (selectedDate, forecastEndDate) => forecastEndDate < selectedDate ? forecastEndDate : selectedDate,
)

const getIsPeriodAdjusted = createSelector(
  [getPeriod, getStartDate, getEndDate, getLatestDate],
  getDatesAdjustedHelper,
)

export const getPassthroughResourcesStatus = createSelector(
  [getAllResourcesStatuses],
  (statuses) => combineResourcesStatuses("dailyForecast", statuses),
)

export const getIsLoading = createSelector(
  [getPassthroughResourcesStatus],
  isLoading,
)

export const getIsAdjusted = createSelector(
  [getIsPeriodAdjusted, getEndDate, getAdjustedEndDate, getIsLoading],
  (isPeriodAdjusted, endDate, adjustedEndDate, isLoading) => (isPeriodAdjusted || endDate !== adjustedEndDate) && !isLoading,
)
